import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { utils, writeFile } from "xlsx";
import "../../style/Dashboard.css";
import "chart.js/auto";
import PreviewModal from "../PreviewModal";
import { getAssignToList } from "../../types/api";
import { fetchMonthlyLeadsReport, fetchUserList, updateAssignee, updateFollowupStatus } from "../../types/dashboard";
import UserTableList from "../admin/UserTableList";
import debounce from "lodash.debounce";
import JoiningsTable from "./JoiningsTable";
//import MenuPanel from "./MenuPanel";
import { stageData, StageKey } from "../../types/dashboard/stageData";
import StageStatusFilters from "./StageStatusFilters";
import DigiUSerListFilters from "../admin/DigiUSerListFilters";
import ContactActions from "./ContactActions";

type MenuStyle = "GRID" | "ACCORDION";
type CountsMap = Record<string, number>;
type GroupDef = { title: string; items: string[]; roles: string[] };

// Assume ALL_GROUPS, ITEM_ACCESS, stageData, t, parseJwtRole are same as previous code
/* ----------------------------- Static Groups ------------------------------ */

const NO_BADGE_GROUPS = ["Adminstrator", "Settings", "Reports"];

const ALL_GROUPS: Record<string, GroupDef> = {
  otp: {
    title: "OTP Related",
    items: ["OtpCompleted", "OtpFailed"],
    roles: ["ADMIN", "MANAGER", "EDITOR", "USER"]
  },
  enquiry: {
    title: "Enquiry Flow",
    items: [
      "Introduction",
      "DecisionSurvey",
      "Occupation",
      "ContactDetails",
      "CNT"
    ],
    roles: ["ADMIN", "MANAGER", "EDITOR"]
  },
  assignment: {
    title: "Assignment",
    items: ["PendingAssignee", "AssignedEnquiry", "PendingApproval"],
    roles: ["ADMIN", "MANAGER", "EDITOR", "USER", "VIEWER"]
  },
  View: {
    title: "View",
    items: ["Manual", "Followup", "MyEnquiry", "AllEnquiry"],
    roles: ["ADMIN", "MANAGER", "EDITOR", "USER", "VIEWER"]
  },
  Adminstrator: {
    title: "Adminstrator",
    items: ["User", "Role"],
    roles: ["ADMIN"]
  },
  Settings: {
    title: "Settings",
    items: ["Group"],
    roles: ["ADMIN"]
  },
  Reports: {
    title: "Reports",
    items: ["MonthlyLeadsReport", "MonthlyBusinessReport","AnnualLeadsReport", "AnnualBusinessReport"],
    roles: ["ADMIN", "MANAGER", "EDITOR", "USER", "VIEWER"]
  }
};

/* ----------------------------- Item Access -------------------------------- */

const ITEM_ACCESS: Record<string, string[]> = {
  ADMIN: Object.values(ALL_GROUPS).flatMap((g) => g.items),
  MANAGER: [
    "OtpCompleted",
    "OtpFailed",
    "Introduction",
    "DecisionSurvey",
    "Occupation",
    "ContactDetails",
    "CNT",
    "PendingAssignee",
    "AssignedEnquiry",
    "Manual", 
    "Followup", 
    "MyEnquiry",
    "AllEnquiry"
  ],
  EDITOR: [
    "OtpCompleted",
    "OtpFailed",
    "Introduction",
    "DecisionSurvey",
    "Occupation",
    "PendingAssignee", 
    "Followup",
    "MyEnquiry",
    "AssignedEnquiry"
  ],
  USER: ["PendingAssignee", "AssignedEnquiry", "MyEnquiry", "AllEnquiry", "Followup"],
  VIEWER: ["AssignedEnquiry", "MyEnquiry", "AllEnquiry", "Followup"]
};

/* ----------------------------- JWT Role Parser ---------------------------- */

function parseJwtRole(token?: string | null): string | null {
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );

    if (decoded.role) return decoded.role;
    if (decoded.roles && Array.isArray(decoded.roles)) return decoded.roles[0];
    if (decoded.authorities) return decoded.authorities[0];

    if (decoded.realm_access?.roles)
      return decoded.realm_access.roles[0];

    return null;
  } catch {
    return null;
  }
}

/* ----------------------------- i18n -------------------------------------- */

const t = {
  dashboardTitle: "Dashboard",
  menuStyleLabel: "Menu Style",
  grid: "Grid",
  accordion: "Accordion",
  noData: "No Data Found.",
  add: "Add",
  edit: "Edit",
  cancel: "Cancel",
  save: "Save",
  download: "Download",
  loading: "Loading..."
};

/* -------------------------------------------------------------------------- */
/*                             Dashboard Component                            */
/* -------------------------------------------------------------------------- */

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // ----------------- State -----------------
  const [menuStyle, setMenuStyle] = useState<MenuStyle>("GRID");
  const [counts, setCounts] = useState<CountsMap>({});
  const [error, setError] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState("");

  const [menuName, setMenuName] = useState<string | null>(null);
  const [newJoinings, setNewJoinings] = useState<any[]>([]);
  const [digiUserList, setDigiUserList] = useState<any[]>([]);
 // const [monthyLeadReport, setMonthlyLeadsReport] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedContactInfo, setSelectedContactInfo] = useState<any | null>(null);
  const [selectRow, setSelectRow] = useState("");

  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const [users, setUsers] = useState<{ id: string; userName: string; mobileNumber: string }[]>([]);

  const [assignee, setAssignee] = useState("");
  const [stage, setStage] = useState("");
  const [followupStatus, setFollowupStatus] = useState("");
  const [notes, setNotes] = useState("");

  const [digiUserJob, setDigiUSerJob] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const [selectAssignee, setSelectAssignee] = useState("");
  const [selectStage, setSelectStage] = useState("");
  const [selectNotes, setSelectNotes] = useState("");
  const [selectJob, setSelectJob] = useState("");
  const [selectFollowupStatus, setSelectFollowupStatus] = useState("");

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [customerFilter, setCustomerFilter] = useState({
    name: "",
    mobile: "",
    assignedTo: "",
    stage: "",
    status: "",
  });

  const [digiUserFilter, setDigiUserFilter] = useState({
    userName: "",
    firstName: "",
    mobileNumber: "",
    parentId: "",
    role: "",
  });
  // ----------------- Pagination -----------------
  //const [currentPage, setCurrentPage] = useState(1);

  // ----------------- Role -----------------
  const jwtToken = localStorage.getItem("token");
  const jwtRole = parseJwtRole(jwtToken);
  const storedRole = localStorage.getItem("role") ?? "USER";
  const userRole = (jwtRole || storedRole).toUpperCase();
  
  const userName =  localStorage.getItem('userName') || '';
  const maskMobile = (mobile?: string) => {
    if (!mobile) return "--";
    mobile = mobile.replace(/\+/g, "").replace(/\d(?=\d{8})/g, "");
    return mobile.replace(/\d(?=\d{3})/g, "*");
  };

  // ----------------- Lifecycle -----------------
  useEffect(() => {
    let mounted = true;

    getAssignToList().then(setUsers).catch(() => console.log("Failed to load users"));
    fetchCounts(mounted);

    return () => {
      mounted = false;
    };
  }, [userRole]);

  const fetchCounts = async (isMounted: boolean) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user-details/counts/${userRole}/${localStorage.getItem("userName") ?? ""}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (isMounted) setCounts(data || {});
    } catch (err) {
      if (isMounted) setError("Failed to load counts");
      console.error(err);
    }
  };

  const menuClick = (key: string) => {
    setMenuName(key);
    resetForm();
    setLoading(true);
    setShowFilters(false);
    setNewJoinings([]);
    setDigiUserList([]);
    if (key === "Role" || key === "User" || key === "Group" || key === "MonthlyLeadsReport" || key === "MonthlyBusinessReport" || key === "AnnualLeadsReport" || key === "AnnualBusinessReport") {
      getSettingsMenusList(key);
    } else {
      setShowFilters(true);
      fetchNewJoinings(key);
    }
  };

  const getSettingsMenusList = async(key:string) => {
    try {
      if (key === "User") {
        const resp = await fetchUserList();
        setDigiUserList(Array.isArray(resp) ? resp : []);
      } else  if (key === "MonthlyLeadsReport") {
        //alert(key);
           navigate('/reports-monthly-lead');
        //fetchMonthlyLeadsReport(2016, 1).then((data) => { setMonthlyLeadsReport(data); });
      }  else  {
        setMenuName("");
      } 
    } catch (err) {
      setError("Failed to fetch list");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewJoinings = async (key: string) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user-details/list/${key}/${userRole}/${localStorage.getItem("userName") ?? ""}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      console.log("data length:", data.length);
      setNewJoinings(Array.isArray(data) ? data : []);
     // setCurrentPage(1); // Reset pagination
    } catch (err) {
      setError("Failed to fetch list");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (joining: any) => {
    setSelectRow(joining.name);
    setSelectedContactInfo(joining.contactInfo || null);

    setAssignee(joining.assignTo);
    setSelectAssignee(joining.assignTo);
    
    setSelectJob(joining.job);

    setMobileNumber(joining.mobileNumber);
    setNotes(joining.notes);
    setSelectNotes(joining.notes);

    setStage(joining.stage);
    setSelectStage(joining.stage);

    setFollowupStatus(joining.followupStatus);
    setSelectFollowupStatus(joining.followupStatus);

    setShowPreviewModal(true);
  };

  const handleDigiUserRowClick = (userDtl: any) => {
    setSelectRow(userDtl.userName);
    setDigiUSerJob(userDtl.job);
    setMobileNumber(userDtl.mobileNumber);

    setShowPreviewModal(true);
  };

  const resetForm = () => {
    setSelectRow("");
    setSelectedContactInfo(null);
    setAssignee("");
    setSelectAssignee("");
    setDigiUSerJob("");
    setSelectJob("");
    setMobileNumber("");
    setNotes("");
    setSelectNotes("");
    setStage("");
    setSelectStage("");
    setFollowupStatus("");
    setSelectFollowupStatus("");
    setError("");
    setAlertMessage("");
    clearFilters();
  };

  const closePopup = () => {
    setShowPreviewModal(false);
    resetForm();
    if (alertMessage.trim() !== "") {
      fetchCounts(true);
      if (menuName) menuClick(menuName);
    }
  };

  const saveEdit = async () => {
    setEditMode(false);
    try {
      if (userRole === "ADMIN") {
        await updateAssignee(stage, assignee, followupStatus, notes, mobileNumber);
      } else {
        await updateFollowupStatus(stage, followupStatus, notes, mobileNumber);
      }
      setAlertMessage("Successfully updated.");
    } catch {
      setError("Failed to update.");
    }
  };

  const resetEdit = () => {
    setAssignee(selectAssignee);
    //setJob(selectJob);
    setNotes(selectNotes);
    setStage(selectStage);
    setFollowupStatus(selectFollowupStatus);
  };

  const handleDownloadExcel = () => {
    if (filteredJoinings.length === 0) {
      alert("No data available to download.");
      return;
    }
    
    const getStageLabel = (stage: string) => {
      return stageData[stage]?.label || "";
    };

    const getSubdivisionLabel = (stage: string, value: string) => {
      return (
        stageData[stage]?.subdivisions.find((s) => s.value === value)?.label || ""
      );
    };

    const hideMobile = (assignedTo: string, mobile: string) => {
      if (userRole === "ADMIN") return mobile;
      return userName === assignedTo  ? mobile : maskMobile(mobile);
    };

    const formatted = filteredJoinings.map((j, idx) => ({
      "#": idx + 1,
      "Full Name": j.name,
      "Mobile Number": hideMobile(j.assignTo, j.mobileNumber),
      "Assigned To": j.assignTo || "",
      "Follow-Up": getStageLabel(j.stage),
      "city": j.city || "",
      Status: getSubdivisionLabel(j.stage, j.followupStatus),
      Notes: j.notes || "",
    }));

    const ws = utils.json_to_sheet(formatted);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Joinings");
    writeFile(wb, `New_Joinings_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ----------------- Filtering -----------------
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setCustomerFilter((prev) => ({ ...prev, ...newFilter })), 300),
    []
  );
  const debouncedSetDigiUserFilter = useCallback(
    debounce((newFilter) => setDigiUserFilter((prev) => ({ ...prev, ...newFilter })), 300),
    []
  );

  const filteredJoinings = useMemo(() => {
    return newJoinings.filter((j) => {
      return (
        (customerFilter.name === "" || j.name?.toLowerCase().includes(customerFilter.name.toLowerCase())) &&
        (customerFilter.mobile === "" || j.mobileNumber?.includes(customerFilter.mobile)) &&
        (customerFilter.assignedTo === "" || j.assignTo?.toLowerCase().includes(customerFilter.assignedTo.toLowerCase())) &&
        (customerFilter.stage === "" || j.stage?.includes(customerFilter.stage)) &&
        (customerFilter.status === "" || j.followupStatus?.includes(customerFilter.status))
      );
    });
  }, [newJoinings, customerFilter]);

  const digiUserFilteredList = useMemo(() => {
    return digiUserList.filter((j) => {
      return (
        (digiUserFilter.userName === "" || j.userName?.toLowerCase().includes(digiUserFilter.userName.toLowerCase())) &&
        (digiUserFilter.firstName === "" || j.firstName?.includes(digiUserFilter.firstName.toLowerCase()))  &&
        (digiUserFilter.mobileNumber === "" || j.mobileNumber?.includes(digiUserFilter.mobileNumber)) &&
        (digiUserFilter.role === "" || j.role?.toLowerCase().includes(digiUserFilter.role.toLowerCase())) &&
        (digiUserFilter.parentId === "" || j.parentId?.includes(digiUserFilter.parentId.toLowerCase()))
      );
    });
  }, [digiUserList, digiUserFilter]);

  const clearFilters = () => setCustomerFilter({ name: "", mobile: "", assignedTo: "", stage: "", status: "" });
  const clearDigiUserFilters = () => setDigiUserFilter({ userName: "", firstName: "", mobileNumber: "", parentId: "", role: "", });

  // ----------------- Role-based Menu -----------------
  const allowedGroupKeys = useMemo(() => Object.entries(ALL_GROUPS).filter(([_, def]) => def.roles.includes(userRole)).map(([gk]) => gk), [userRole]);
  const allowedItemsSet = useMemo(() => new Set(ITEM_ACCESS[userRole] ?? []), [userRole]);

  const filteredGroups = useMemo(() => {
    const out: Record<string, GroupDef> = {};
    for (const key of allowedGroupKeys) {
      const def = ALL_GROUPS[key];
      if (!def) continue;
      const filteredItems = def.items.filter((it) => allowedItemsSet.has(it));
      if (filteredItems.length > 0) out[key] = { ...def, items: filteredItems };
    }
    return out;
  }, [allowedGroupKeys, allowedItemsSet]);

  // ----------------- Render Helpers -----------------
  const prettify = (k: string) => ({
    Manual: "Manual",
    PendingApproval: "Pending Approval",
    OtpCompleted: "OTP Completed",
    OtpFailed: "OTP Failed",
    Introduction: "Introduction",
    DecisionSurvey: "Decision Survey",
    Occupation: "Occupation",
    CNT: "CNT",
    ContactDetails: "Contact Details",
    PendingAssignee: "Pending Assignee",
    AssignedEnquiry: "Assigned Enquiry",
    AllEnquiry: "All Enquiry",
  }[k] ?? k.replace(/([a-z0-9])([A-Z])/g, "$1 $2"));

  const renderMenuItem = (key: string, groupTitle?: string) => {
    const badge = counts[key] ?? 0;
    const hideBadge = NO_BADGE_GROUPS.includes(groupTitle ?? "");
    return (
      <button key={key} className={`group-item ${menuName === key ? "group-item-active" : ""}`} title={prettify(key)} onClick={(e) => { e.stopPropagation(); menuClick(key); }}>
        <span className="group-item-label">{prettify(key)}</span>
        {!hideBadge && (
          <span className="count-badge">{badge}</span>
        )}
      </button>
    );
  };

const GridMenu = () => (
  <div className="grid-menu">
    {Object.entries(filteredGroups).map(([gk, g]) => {
      // 1. Move the logic here (inside the function body of the map)
      const hideBadge = NO_BADGE_GROUPS.includes(g.title ?? "");

      return (
        <div key={gk} className="group-card">
          <h3 className="group-card-title">{g.title}</h3>
          <div className="group-card-items">
            {g.items.map((it) => (
              <div key={it} className="card-item" onClick={() => menuClick(it)} title={prettify(it)}>
                <div className="card-item-label">{prettify(it)}</div>
                
                {!hideBadge && (
                  <div className="card-item-count">{counts[it] ?? 0}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

  const AccordionMenu = () => (
    <div className="accordion-wrapper">
      {Object.entries(filteredGroups).map(([gk, g]) => {
        const open = openGroup === gk;
        return (
          <div key={gk} className={`accordion-card ${open ? "open" : ""}`}>
            <div className="accordion-header" onClick={() => setOpenGroup(open ? null : gk)}>
              <div className="accordion-title">{g.title}</div>
              <div className={`accordion-arrow ${open ? "open" : ""}`}>▶</div>
            </div>
            {open && <div className="accordion-body"><div className="group-items">{g.items.map((item) => renderMenuItem(item, g.title))}</div></div>}
          </div>
        );
      })}
    </div>
  );

  // ----------------- Render -----------------
  return (
    <div className="dashboard-container enhanced-dashboard">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="left-top"></div>
        <div className="right-top">
          <label className="view-label">{t.menuStyleLabel}:</label>
          <select className="view-select-dropdown" value={menuStyle} onChange={(e) => setMenuStyle(e.target.value as MenuStyle)}>
            <option value="GRID">{t.grid}</option>
            <option value="ACCORDION">{t.accordion}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Menu */}
      <div className="menu-style-area">{menuStyle === "GRID" ? <GridMenu /> : <AccordionMenu />}</div>

      {/* Loading Spinner */}
      {loading && <div className="spinner" role="status" />}

      {/* Joinings Table */}
      {menuName && (
        <div className="new-joinings-section">
          {menuName === "User" && (
            <>
              {/* Table */}
              <div className="advanced-filter-wrapper">
                <button className="filter-toggle-btn" onClick={() => setShowFilters((prev) => !prev)}>{showFilters ? "Hide Filters ▲" : "Show Filters ▼"}</button>
                {showFilters && (
                  <div className="filter-grid">
                    <DigiUSerListFilters filter={digiUserFilter} userRole={userRole} onChange={debouncedSetDigiUserFilter} />
                    <div className="controls-right"><button onClick={clearDigiUserFilters}>Clear</button></div>
                  </div>
                )}
              </div>
              <UserTableList
                data={digiUserFilteredList}
                filter={digiUserFilter}
                currentUserRole={userRole}
                menuName={prettify(menuName)}
                onRowClick={(row) => handleDigiUserRowClick(row)}
              />
            </>
          )}

         {menuName !== "User" && (
            <>
              <div className="advanced-filter-wrapper">
                <button className="filter-toggle-btn" onClick={() => setShowFilters((prev) => !prev)}>{showFilters ? "Hide Filters ▲" : "Show Filters ▼"}</button>
                {showFilters && (
                  <div className="filter-grid">
                    <StageStatusFilters filter={customerFilter} userRole={userRole} onChange={debouncedSetFilter} />
                    <div className="controls-right"><button onClick={clearFilters}>Clear</button></div>
                  </div>
                )}
              </div>
              
              {/* Table */}
              <JoiningsTable
                data={filteredJoinings}
                filter={customerFilter}
                userName={userName}
                currentUserRole={userRole}
                menuName={prettify(menuName)}
                onRowClick={(row) => handleRowClick(row)}
              />

      
              {/* Controls */}
              <div className="list-controls">
                {userRole === "ADMIN" && <button onClick={() => navigate("/contactUploadForm")}>{t.add}</button>}
                <div className="controls-right"><button onClick={handleDownloadExcel}>{t.download}</button></div>
              </div>
                
              {/* Preview Modal */}
              <PreviewModal open={showPreviewModal} onClose={closePopup} title="Contact Info" draggable={false} footerButtons={[
                { label: "Edit", visible: editMode, type: "primary", onClick: () => setEditMode(true) },
                { label: "Save", visible: !editMode, type: "primary", onClick: saveEdit },
                { label: "Reset", visible: !editMode, type: "default", onClick: resetEdit },
              ]}>
                {selectedContactInfo && <pre>
                    <div className="contact-info-section">
                        {alertMessage && (
                        <div className="alert-message">{alertMessage}</div>
                        )}
                        {error && <div className="error-message">{error}</div>}
        
                        {/* ----------------------------- Contact Info ----------------------------- */}
        
                        <div className="contact-info-section-dtl">
                        <p>
                            <strong>First Name: </strong>
                            {selectedContactInfo?.firstName || "--"}
                        </p>
                        <p>
                            <strong>Middle Name: </strong>
                            {selectedContactInfo?.middleName || "--"}
                        </p>
                        <p>
                            <strong>Last Name: </strong>
                            {selectedContactInfo?.lastName || "--"}
                        </p>
                        <p>
                            <div className="mobile-contact-row">
                              <span className="contact-label">Mobile:</span>

                              <span className="contact-number">
                                {maskMobile(selectedContactInfo?.contactNumber)}
                              </span>
                              {(selectAssignee === userName || userRole === "ADMIN") && (
                                <>
                                  <ContactActions contactNumber={selectedContactInfo?.contactNumber} />
                                </>
                              )}
                              
                            </div>
                        </p>
                        <p>
                            <strong>Job: </strong>
                            {selectedContactInfo?.job || "--"}
                        </p>
                        </div>
        
                        {/* ----------------------------- Follow-Up Section ----------------------------- */}
        
                        <div className="contact-info-section-dtl">
                        {userRole === "ADMIN" && (
                            !editMode ? (
                            <p>
                                <strong>Assignee: </strong>
                                <select
                                value={assignee}
                                onChange={(e) => setAssignee(e.target.value)}
                                style={{ width: 200 }}
                                >
                                <option value="">-- Select User --</option>
                                {users.map((u) => (
                                    <option key={u.userName} value={u.userName}>
                                    {u.userName} - {u.mobileNumber}
                                    </option>
                                ))}
                                </select>
                            </p>
                            ) : (
                            <p>
                                <strong>Assignee: </strong>
                                {selectAssignee || "--"}
                            </p>
                            )
                        )}
        
                        <p>
                            <strong>Follow-Up: </strong>
                            <select
                            value={stage}
                            onChange={(e) => {
                                setStage(e.target.value);
                                setFollowupStatus("");
                            }}
                            >
                            <option value="">-- Select Stage --</option>
                            {Object.entries(stageData).map(([value, { label }]) => (
                                <option key={value} value={value}>
                                {label}
                                </option>
                            ))}
                            </select>
                        </p>
        
                        <p>
                            <strong>Status: </strong>
                            <select
                            value={followupStatus}
                            onChange={(e) => setFollowupStatus(e.target.value)}
                            >
                            <option value="">-- Select Status --</option>
                            {stage &&
                                stageData[stage as StageKey].subdivisions.map((sub) => (
                                <option key={sub.value} value={sub.value}>
                                    {sub.label}
                                </option>
                                ))}
                            </select>
                        </p>
        
                        <p>
                            <strong>Notes: </strong>
                            <textarea
                            className="notes-textarea"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Enter notes..."
                            />
                        </p>
                        </div>
                    </div>
                    
                    </pre>}
              </PreviewModal>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;

